import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";

export function SettingsPage() {
  const { t } = useTranslation();
  const { me } = useAuth();
  const qc = useQueryClient();

  const [form, setForm] = useState({
    name: "",
    street: "",
    zip: "",
    city: "",
    country: "DE",
    phoneE164: "",
    findingsEmail: "",
  });

  useEffect(() => {
    if (me) {
      setForm({
        name: me.name,
        street: me.street,
        zip: me.zip,
        city: me.city,
        country: me.country,
        phoneE164: me.phoneE164 ?? "",
        findingsEmail: me.findingsEmail ?? "",
      });
    }
  }, [me]);

  const save = useMutation({
    mutationFn: () =>
      api("/api/me", {
        method: "PATCH",
        body: JSON.stringify({
          ...form,
          phoneE164: form.phoneE164 || null,
          findingsEmail: form.findingsEmail || null,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });

  const exportData = async () => {
    const res = await fetch("/api/me/export", { credentials: "include" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `petloc-export.json`;
    a.click();
  };

  const deleteAccount = useMutation({
    mutationFn: () => api("/api/me", { method: "DELETE" }),
    onSuccess: () => (window.location.href = "/"),
  });

  if (!me) return null;

  return (
    <div className="max-w-2xl space-y-6">
      <h1 className="font-display text-3xl font-semibold tracking-tight">{t("nav.settings")}</h1>

      <div className="card p-6 space-y-4">
        <h2 className="font-semibold">Profil</h2>
        <Field label={t("auth.email")} hint={t("auth.emailFindingsHint")}>
          <input className="input bg-slate-50 text-slate-600 cursor-not-allowed" value={me.email} disabled readOnly />
        </Field>
        <Field label={t("auth.findingsEmail")} hint={t("auth.findingsEmailHint")}>
          <input
            type="email"
            className="input"
            placeholder={me.email}
            value={form.findingsEmail}
            onChange={(e) => setForm({ ...form, findingsEmail: e.target.value })}
          />
        </Field>
        <Field label={t("auth.name")}>
          <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label={t("auth.street")}>
          <input className="input" value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label={t("auth.zip")}>
            <input className="input" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} />
          </Field>
          <div className="col-span-2">
            <Field label={t("auth.city")}>
              <input className="input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </Field>
          </div>
        </div>
        <Field label={t("auth.phone")} hint={t("auth.phoneHint")}>
          <input className="input" value={form.phoneE164} onChange={(e) => setForm({ ...form, phoneE164: e.target.value })} />
        </Field>
        <button className="btn-primary" onClick={() => save.mutate()}>{t("common.save")}</button>
      </div>

      <div className="card p-6 space-y-3">
        <h2 className="font-semibold">Bestellungen &amp; QR-Codes</h2>
        <p className="text-sm text-slate-600">
          Übersicht deiner bestellten Halsbänder und Mini-Anhänger — einzelne Codes sperren bzw.
          entsperren oder Ersatz nachbestellen.
        </p>
        <Link to="/app/orders" className="btn-secondary inline-block">Bestellungen verwalten</Link>
      </div>

      <div className="card p-6 space-y-3">
        <h2 className="font-semibold">DSGVO</h2>
        <p className="text-sm text-slate-600">
          Du kannst deine Daten jederzeit exportieren oder deinen Account löschen. Ein gelöschter
          Account wird anonymisiert und alle Haustiere werden deaktiviert.
        </p>
        <div className="flex gap-3">
          <button className="btn-secondary" onClick={exportData}>Daten exportieren</button>
          <button
            className="btn-danger"
            onClick={() => {
              if (confirm("Account wirklich löschen? Dies kann nicht rückgängig gemacht werden.")) {
                deleteAccount.mutate();
              }
            }}
          >
            Account löschen
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}
